import { useCallback, useEffect, useState } from "react";
import api from "@/lib/api";
import { fmtDateTime } from "@/lib/format";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { toast } from "sonner";
import { Bell, BellOff, CheckCheck, CalendarCheck, Clock, Megaphone, Star } from "lucide-react";

const EVENT_META = {
  booking_confirmed: { label: "Prenotazione confermata", icon: CalendarCheck },
  booking_modified: { label: "Appuntamento aggiornato", icon: CalendarCheck },
  booking_cancelled: { label: "Appuntamento cancellato", icon: CalendarCheck },
  reminder: { label: "Promemoria", icon: Clock },
  review_request: { label: "Lascia una recensione", icon: Star },
  waitlist_match: { label: "Si è liberato un posto!", icon: Bell },
  giftcard_delivery: { label: "Gift Card", icon: Star },
  campaign: { label: "Novità da Hair Identity", icon: Megaphone },
};

export default function PortalNotifications() {
  const [data, setData] = useState(null);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/portal/notifications");
      setData(data);
    } catch (e) { setData({ notifications: [], unread: 0 }); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const markRead = async (n) => {
    if (n.read) return;
    try { await api.post(`/portal/notifications/${n.id}/read`); load(); } catch (e) { console.debug("mark read failed", e); }
  };
  const markAll = async () => {
    try { await api.post("/portal/notifications/read-all"); toast.success("Tutte lette"); load(); }
    catch (e) { toast.error("Errore"); }
  };

  const list = data?.notifications || [];
  const unread = data?.unread || 0;

  return (
    <div>
      <div className="flex items-center justify-between mb-5">
        <div>
          <h1 className="font-heading text-3xl font-semibold flex items-center gap-2">Notifiche
            {unread > 0 && <Badge className="bg-[hsl(var(--hi-gold))] text-[hsl(var(--hi-gold-contrast,0_0%_100%))]">{unread}</Badge>}
          </h1>
          <p className="text-sm text-muted-foreground mt-1">Conferme, promemoria e novità da Hair Identity</p>
        </div>
        {unread > 0 && <Button variant="outline" size="sm" onClick={markAll} data-testid="notif-read-all"><CheckCheck className="h-4 w-4 mr-1" /> Segna lette</Button>}
      </div>

      {data === null ? (
        <div className="space-y-3">{Array.from({ length: 4 }).map((_, i) => <Skeleton key={i} className="h-20 w-full" />)}</div>
      ) : list.length === 0 ? (
        <Card className="p-12 text-center text-sm text-muted-foreground" data-testid="notif-empty">
          <BellOff className="h-8 w-8 mx-auto mb-3 opacity-40" />
          Nessuna notifica per ora. Ti avviseremo qui quando ci saranno novità.
        </Card>
      ) : (
        <div className="space-y-3">
          {list.map((n) => {
            const meta = EVENT_META[n.event] || { label: n.subject || "Notifica", icon: Bell };
            const Icon = meta.icon;
            return (
              <Card key={n.id} onClick={() => markRead(n)} data-testid={`notif-${n.id}`}
                className={`p-4 flex items-start gap-3 cursor-pointer transition-colors hover:bg-accent/40 ${!n.read ? "border-[hsl(var(--hi-gold)/0.4)] bg-[hsl(var(--hi-gold-soft)/0.35)]" : ""}`}>
                <div className="h-9 w-9 rounded-lg bg-[hsl(var(--hi-gold-soft))] flex items-center justify-center shrink-0"><Icon className="h-[18px] w-[18px] text-[hsl(var(--hi-gold))]" /></div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <span className="font-medium text-sm">{meta.label}</span>
                    {n.category === "marketing" && <Badge variant="secondary" className="text-[9px]">Novità</Badge>}
                    {!n.read && <span className="h-2 w-2 rounded-full bg-[hsl(var(--hi-gold))]" />}
                  </div>
                  <p className="text-sm text-muted-foreground mt-0.5 whitespace-pre-line">{n.body && n.body !== n.event ? n.body : "Apri l'app per i dettagli."}</p>
                  <div className="text-[11px] text-muted-foreground mt-1">{fmtDateTime(n.created_at)}</div>
                </div>
              </Card>
            );
          })}
        </div>
      )}
    </div>
  );
}
